import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { beatCount, check, hasEasedMotion } from '../skillUsage';

export const motionPolishSkill: CodeoSkill = {
    name: SKILL_NAMES.motionPolish,
    kind: 'codeo',
    description:
        'The craft layer that separates amateur motion from produced motion — the house easing ' +
        'profile, overlap, stagger, anticipation. Load it whenever animation feels flat, robotic, ' +
        'or "everything fades in at once", and before reaching for any bouncy ease.',
    checkLooksFor: 'a named easing on the motion, and either a stagger or four beats or more',
    documentUsesSkill: check(
        (script) => hasEasedMotion(script) && (/stagger/.test(script) || beatCount(script) >= 4),
    ),
    instructions: `# Motion Polish

Most flat motion isn't missing animation — it has the wrong *craft*. Same elements, same timeline, but everything moves linearly, simultaneously, and ends abruptly. These are the levers, in order of impact, that make motion read as produced. All of it is core GSAP on the single paused timeline (\`window.__timelines.main\`) — no CSS animation.

## 1. Easing vocabulary

**The house settle is \`power3.out\`, with \`power4.out\` and \`expo.out\` for more punch.** All three share one profile, and it is worth knowing as a shape rather than a name: the element covers most of its distance in the first three or four frames — call it 0.15s — and then spends the rest of the tween decelerating into place. The attack is what reads as confident; the long tail is what reads as expensive.

- **Entrances** → \`power3.out\` (default) / \`power4.out\` / \`expo.out\` — fast attack, long settle. The element *arrives*.
- **Exits** → \`power2.in\` / \`power3.in\` — accelerate away. Exits run faster than entrances, usually ~60% of the duration.
- **Ambient / loops** → \`sine.inOut\` — smooth both ends, no acceleration tells.
- **Constant motion** → \`none\`. Narrow but real: a continuous spin or marquee, and the slow drift that keeps a held frame alive (see \`${SKILL_NAMES.sustainedSceneReveal}\`). Linear on an *entrance* is the single biggest amateur tell.

**Overshoot is a register, not a polish step.** \`back.out(…)\` and \`elastic\` do not add emphasis to serious motion — they change what the graphic is saying, from confident to playful, and they are the most reliable single tell of amateur motion graphics. Use them only when playful is the deliberate intent for this brief, and then commit: a bouncing badge on an otherwise smooth graphic reads as an accident.

The trap in both directions: one ease everywhere reads flat, but overshoot everywhere reads cheap, and the second failure is much worse than the first. Get variety by moving *within* the smooth families by energy — \`sine\`/\`power1\` calm, \`power3\` standard, \`power4\`/\`expo\` punch — not by reaching for bounce.

## 2. Overlap (the biggest lever)

Amateur timelines sequence beats end-to-end: in, pause, out. Produced timelines *overlap* — the next beat starts while the previous is still finishing (~40–60% in). Use absolute positions or relative offsets, not the default append:

\`\`\`js
// flat: each beat waits for the last
tl.from('#title', { y: 30, opacity: 0, duration: 0.6 });
tl.from('#sub',   { y: 30, opacity: 0, duration: 0.6 });   // starts at 0.6

// polished: subtitle rises while the title is still settling
tl.from('#title', { y: 40, opacity: 0, duration: 0.7, ease: 'power3.out' }, 0.0);
tl.from('#sub',   { y: 28, opacity: 0, duration: 0.6, ease: 'power3.out' }, 0.25);
\`\`\`

## 3. Stagger (reveal groups, never all-at-once)

A row of cards, list items, or logos should cascade, not pop together:

\`\`\`js
tl.from('.feature-card', { y: 40, opacity: 0, duration: 0.55, ease: 'power3.out',
    stagger: 0.09 }, 0.4);
\`\`\`

Stagger 0.06–0.12s reads as a deliberate cascade; larger drags, smaller looks simultaneous. For symmetry, \`stagger: { each: 0.08, from: 'center' }\`.

## 4. Anticipation

Real things announce themselves before they arrive. The cheap version of this is an overshoot; the good version is **something else moving first**.

**Debris.** A few small specks appear a beat *before* the subject exists, and spray outward again as it leaves. It costs almost nothing and it is the difference between an element switching on and an element arriving from somewhere. Index-derived positions keep it deterministic.

\`\`\`js
// Specks lead the subject in by ~0.12s, then scatter as it settles.
const ARRIVE = 1.0;
gsap.set('.mp-speck', { transformOrigin: '50% 50%' });
tl.fromTo('.mp-speck', { scale: 0, opacity: 0 },
    { scale: 1, opacity: 1, duration: 0.12, ease: 'power2.out', stagger: 0.02 }, ARRIVE - 0.12);
tl.to('.mp-speck', {
    x: (i) => (i % 2 ? 34 : -30) + i * 3,
    y: (i) => (i % 3 ? -26 : 22),
    opacity: 0, duration: 0.4, ease: 'power2.out', stagger: 0.02,
}, ARRIVE + 0.06);
tl.fromTo('#mp-subject', { scale: 0.7, opacity: 0 },
    { scale: 1, opacity: 1, duration: 0.5, ease: 'power3.out' }, ARRIVE);
\`\`\`

Four to eight specks, 4–8px, in the subject's own colour. More than about ten and it reads as a particle effect rather than as debris.

**Settle.** If a beat genuinely wants an overshoot, the explicit two-step gives far finer control than \`back.out()\` — and keeping it explicit makes it a decision rather than a default. Cap it at ~1.05–1.08 for serious registers; the overshoot should be *felt*, not seen.

\`\`\`js
tl.fromTo('#badge', { scale: 0 },
    { scale: 1.06, duration: 0.35, ease: 'power2.out' }, 1.0);
tl.to('#badge', { scale: 1, duration: 0.25, ease: 'power2.inOut' }, 1.35);
\`\`\`

## 5. Secondary motion

When a primary element moves, link a secondary one a beat later — a shadow settling, a caption catching up, an underline drawing after the word lands. The lag (0.1–0.25s) is what makes it feel physical rather than rigid.

## Before → after, one beat

\`\`\`js
// FLAT
tl.from('#headline', { opacity: 0, duration: 0.5 });
tl.from('.stat',     { opacity: 0, duration: 0.5 });

// POLISHED
tl.from('#headline', { y: 36, opacity: 0, duration: 0.7, ease: 'power3.out' }, 0.0);
tl.from('.stat',     { y: 24, opacity: 0, duration: 0.55, ease: 'power4.out',
    stagger: 0.1 }, 0.3);
\`\`\`

Same elements, same timeline. The difference is a directional entrance, a fast-attack ease, an overlap, and a cascade — none of it overshoot.

## Pitfalls

- **\`back\`/\`elastic\` as a default is the failure this skill exists to prevent.** If overshoot appears on more than one beat in a graphic, or on anything the viewer is reading, take it out. Reserve it for a brief whose register is genuinely playful.
- Overlap improves flow but watch readability — text the viewer must read needs to fully land and hold before the next beat steals attention.
- Polish is per-beat craft; it does not fix a graphic whose beats all fire at the top. If everything enters in the first quarter of the scene and then sits, the problem is pacing — see \`${SKILL_NAMES.sustainedSceneReveal}\`.
- Stagger multiplies onto duration: stagger 0.1 across 8 items adds 0.8s of tail. Account for it so the group finishes inside its window.
- All \`from\` entrances above start from a hidden state (\`opacity: 0\`), so default \`immediateRender\` keeps them hidden until they run — don't reach for \`immediateRender: false\`.
- **That holds for a leaf, not for an ancestor.** \`from\`/\`fromTo\` applies its from-state at *build* time, so a late \`fromTo(el, { opacity: 0 }, ...)\` pins \`el\` to \`opacity: 0\` from t=0 — and opacity on an ancestor gates its entire subtree. Give a wrapper a late entrance and every child inside it is invisible until that entrance runs, however well the children's own tweens are written. The symptom is content that looks like it was never authored. Animate a leaf, or an inner wrapper, or pass \`immediateRender: false\`; and never let one element be a passive container early and an animated target later.
- Polish still lives entirely on the single paused timeline with finite repeats — it never means reaching for CSS \`@keyframes\`/\`transition\` or an infinite repeat, which don't seek.`,
};
